import { sessionStateCookieManager } from "./SessionStateCookieManager";
import { getHideExtensionModalCookieState } from "./bannerUtils";
import { getDeviceBrowser, isDeviceOnExperimentalMode } from "./userAgentUtils";
import {
    CookieSynchronizationError,
    CookieSyncSupportedBrowsers,
    CookieSyncSupportedBrowserType,
} from "../types/cookies";
import {
    ExtensionResponse,
    GetCookies,
} from "@amzn/aws-low-angle-cookie-synchronizer";
import browser from "webextension-polyfill";
import { getLogger } from "./ermineLoggerUtils";
import { MetricName } from "../constants/logging";

/**
 * Currently Cookie Sync feature only supports Chrome and Firefox.
 * @returns true if browser is supported, false otherwise
 */
export const isBrowserSupportedCookieSync = (): boolean => {
    const browserType = getDeviceBrowser();
    return (
        CookieSyncSupportedBrowsers.find(
            (browser) => browser === browserType
        ) !== undefined
    );
};
/**
 * returns if should hide extension modal based on session storage, cookie state, browser/device type and feature flag
 * - If user checks "don't show it again", we won't show it in same browser based on cookie state
 * - If user clicks "start session", we won't show it in same tab based on session storage
 * - If user is using unsupported browser, we won't show it
 * - If user is using mobile device, we won't show it
 * @returns true if extension modal needs to be hidden, false otherwise
 */
export const shouldHideExtensionModal = (): boolean => {
    let shouldHideModal = false;
    shouldHideModal =
        sessionStateCookieManager.getHideExtensionModalSessionState() ||
        getHideExtensionModalCookieState() ||
        !isBrowserSupportedCookieSync() ||
        isDeviceOnExperimentalMode();
    return shouldHideModal;
};

// TODO [LOWA-30534] from: https://tiny.amazon.com/8q2lx3t3
export const getCookiesWithTimeout = async (
    request: GetCookies,
    timeoutMs: number
): Promise<
    | {
          type: "cookies";
          cookies: browser.Cookies.Cookie[];
      }
    | {
          type: "error";
          error: CookieSynchronizationError;
      }
> => {
    const log = getLogger();

    const t0 = performance.now();
    try {
        const cookies = await Promise.race([
            getCookies(request),
            timeoutAfter(timeoutMs),
        ]);
        const t1 = performance.now();
        log.publishNumericMetric(
            MetricName.COOKIE_EXTENSION_RESPONSE_TIME,
            t1 - t0
        );
        log.publishHttpStatusMetric(MetricName.COOKIE_EXTENSION_RESPONSE, 200);
        return {
            type: "cookies",
            cookies,
        };
    } catch (error) {
        const t1 = performance.now();
        log.publishNumericMetric(
            MetricName.COOKIE_EXTENSION_RESPONSE_TIME,
            t1 - t0
        );
        log.publishHttpStatusMetric(MetricName.COOKIE_EXTENSION_RESPONSE, 500);
        return {
            type: "error",
            error,
        };
    }
};

export const getCookies = async (
    request: GetCookies
): Promise<browser.Cookies.Cookie[]> => {
    const browser = getDeviceBrowser() as CookieSyncSupportedBrowserType;
    switch (browser) {
        case "Edge":
        case "Chrome": {
            return requestCookiesPostMessage(request);
        }
        case "Firefox": {
            return requestCookiesPostMessage(request, (cookie) => {
                // Firefox cookie store IDs look like `firefox-default`
                // which differs from what Chrome expects
                cookie.storeId = "0";
                // Firefox only has 3 different SameSite values, but Chrome has 4.
                // The spec for SameSite says that "if SameSite=None then the Secure attribute must also be set".
                // Because of this, we must translate Firefox's "no_restriction" to Chrome's "unspecified".
                // Firefox SameSite: https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/API/cookies/SameSiteStatus
                // Chrome SameSite: https://developer.chrome.com/docs/extensions/reference/cookies/#type-SameSiteStatus
                if (!cookie.secure && cookie.sameSite === "no_restriction") {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (cookie as any).sameSite = "unspecified";
                }
                return cookie;
            });
        }
        default: {
            // The SSR tests manipulate the userAgent in a browser with the
            // extension installed. In general, since we can't guarantee cookie
            // sync to function properly in what could actually be a different
            // browser with a manipulated userAgent, we just return no cookies.
            getLogger().logMessage(
                `Cookie sync not supported in ${browser} | ${window.navigator?.userAgent}`
            );
            return Promise.resolve([]);
        }
    }
};

export const isCookieSynchronizationExtensionInstalled = (): boolean => {
    return Boolean(
        document.documentElement.getAttribute(
            "cookie-synchronization-extension-installed"
        )
    );
};

function timeoutAfter(timeoutMs: number): Promise<never> {
    return new Promise(
        (_resolve, reject: (error: CookieSynchronizationError) => void) => {
            setTimeout(() => {
                reject("Timeout");
            }, timeoutMs);
        }
    );
}

function requestCookiesPostMessage(
    request: GetCookies,
    cookieModifier?: (cookie: browser.Cookies.Cookie) => browser.Cookies.Cookie
) {
    return new Promise<browser.Cookies.Cookie[]>(
        (resolve, reject: (error: CookieSynchronizationError) => void) => {
            window.addEventListener("message", async function (event) {
                if ((event.data as ExtensionResponse)?.responseType) {
                    handleExtensionResponse(
                        event.data as ExtensionResponse,
                        resolve,
                        reject,
                        cookieModifier
                    );
                }
            });
            window.postMessage(request, "*");
        }
    );
}

function handleExtensionResponse(
    response: ExtensionResponse,
    resolve: (result: browser.Cookies.Cookie[]) => void,
    reject: (error: CookieSynchronizationError) => void,
    cookieModifier?: (cookie: browser.Cookies.Cookie) => browser.Cookies.Cookie
) {
    const log = getLogger();

    switch (response.responseType) {
        case "getCookies": {
            resolve(
                cookieModifier
                    ? response.cookies.map(cookieModifier)
                    : response.cookies
            );
            break;
        }
        case "error": {
            log.error(
                `Error occurred in extension: ${response.errorType} | ${response.error}`
            );
            switch (response.errorType) {
                case "getCookies": {
                    reject("ErrorResponse");
                    break;
                }
                case "authentication": {
                    reject("AuthenticationError");
                    break;
                }
                case undefined: {
                    // This case exists for backwards compatibility and can be removed
                    // after the extension is deployed and starts returning this field.
                    reject("ErrorResponse");
                    break;
                }
            }
            break;
        }
        default: {
            log.error(
                `Unexpected extension response: ${JSON.stringify(response)}`
            );
            reject("InvalidResponse");
        }
    }
}
